import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Truck, Shield, Clock, CheckCircle, Phone, MessageCircle, MapPin } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useLanguage } from '@/lib/useLanguage';
import { COUNTY_TO_DISTRICT } from '@/components/counties/countyDistricts';
import { DISTRICT_ORDER, localizedDistrictName, districtContent } from '@/components/seo/districtI18n';
import { districtRegion, ALGARVE_CITIES } from '@/components/seo/regionContext';
import { districtUrl, equipmentUrl, countyUrl } from '@/components/seo/langRoutes';
import { rentalDistrictUrl } from '@/components/seo/crossDomainHreflang';
import { siteBaseUrl, toSitePath } from '@/lib/siteDomain';
import { brandName, brandShort } from '@/components/seo/brandI18n';
import ComplaintsBookButton from '@/components/layout/ComplaintsBookButton';
import OrthocareFooter from '@/components/layout/OrthocareFooter';
import RealReviewsSection from '@/components/seo/RealReviewsSection';
import TrustRatingBadge from '@/components/seo/TrustRatingBadge';
import SeoLangSwitcher from '@/components/seo/SeoLangSwitcher';
import OrthocareHeroSearch from '@/components/landing/OrthocareHeroSearch';
import PlanFromHomeSection from '@/components/seo/PlanFromHomeSection';
import { planFromHome } from '@/components/seo/districtI18n';
import { useNoIndexMeta } from '@/hooks/useNoIndexMeta';

const slugify = (text) =>
  (text || '').toString().toLowerCase()
    .replace(/[àáâãäå]/g, 'a').replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i').replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u').replace(/[ç]/g, 'c')
    .replace(/[^a-z0-9\s-]/g, '').trim()
    .replace(/\s+/g, '-').replace(/-+/g, '-');

const unslug = (s) => (s || '').replace(/-/g, ' ').trim();

export default function DistrictSEOPage() {
  const urlParams = new URLSearchParams(window.location.search);
  const segments = window.location.pathname.split('/').filter(Boolean);
  // Path-based language detection (en/rent, es/alquilar, fr/louer, de/mieten, it/noleggiare)
  const RENT_WORDS = { en: 'rent', es: 'alquilar', fr: 'louer', de: 'mieten', it: 'noleggiare' };
  const localizedMatch = Object.entries(RENT_WORDS).find(([l, w]) => segments[0] === l && segments[1] === w);
  let pathLang = 'pt';
  if (localizedMatch) pathLang = localizedMatch[0];
  else if (segments[0] === 'aluguer') pathLang = 'pt';
  const isEn = pathLang === 'en';
  const districtSlug = segments[segments.length - 1] || urlParams.get('district') || '';
  const langParam = urlParams.get('lang');
  const { lang: uiLang } = useLanguage();
  const lang = pathLang !== 'pt'
    ? pathLang
    : (['en', 'es', 'fr', 'de', 'it'].includes(langParam) ? langParam : (uiLang === 'en' ? 'en' : 'pt'));

  // Resolve district from slug (PT name is the key)
  const district = useMemo(() => {
    const match = DISTRICT_ORDER.find((d) => slugify(d) === slugify(districtSlug));
    return match || DISTRICT_ORDER.find((d) => slugify(d) === slugify(unslug(districtSlug))) || null;
  }, [districtSlug]);

  const { data: counties = [] } = useQuery({
    queryKey: ['seo-counties-all'],
    queryFn: () => base44.entities.County.filter({ active: true }),
    staleTime: 1000 * 60 * 30,
  });

  const districtCounties = useMemo(() => {
    if (!district) return [];
    return (counties || []).filter((c) => COUNTY_TO_DISTRICT[c.name] === district).sort((a, b) => a.name.localeCompare(b.name, 'pt'));
  }, [counties, district]);

  const c = district ? districtContent(lang, district) : null;

  useEffect(() => {
    if (!district || !c) return;
    const d = localizedDistrictName(district, lang);
    document.title = `${c.h1} | ${brandName()}`;
    setMeta('description', c.subtitle);
    const dSlug = slugify(district);
    const langUrls = {
      pt: rentalDistrictUrl('pt', dSlug), en: rentalDistrictUrl('en', dSlug), es: rentalDistrictUrl('es', dSlug),
      fr: rentalDistrictUrl('fr', dSlug), de: rentalDistrictUrl('de', dSlug), it: rentalDistrictUrl('it', dSlug),
      nl: rentalDistrictUrl('nl', dSlug),
    };
    setCanonical(langUrls[lang] || langUrls.pt);
    setHreflang(langUrls);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', langUrls[lang] || langUrls.pt);
    const siteBase = siteBaseUrl();
    const pretrip = planFromHome(lang, district);
    const faqItems = pretrip?.faq ? [pretrip.faq, ...c.faqs] : c.faqs;
    const faq = faqItems.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } }));
    const itemList = districtCounties.slice(0, 60).map((co, i) => ({
      '@type': 'ListItem', position: i + 1, name: co.name,
      url: `${siteBase}/aluguer/cadeira-de-rodas/${slugify(co.name)}`
    }));
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'MedicalBusiness',
      name: `${brandName()} — ${d}`,
      areaServed: { '@type': 'AdministrativeArea', name: district },
      telephone: '+351216035281',
      address: { '@type': 'PostalAddress', addressCountry: 'PT' },
      image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/7526512f2_file_000000000f3871f4acdc11aca9b1a76e.png',
      '@id': langUrls[lang] || langUrls.pt,
      url: langUrls[lang] || langUrls.pt,
    };
    injectSchema('district-medical', schema);
    injectSchema('district-faq', { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: faq });
    if (itemList.length) injectSchema('district-itemlist', { '@context': 'https://schema.org', '@type': 'ItemList', itemListElement: itemList });
    injectSchema('district-breadcrumb', {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: brandShort(), item: `${siteBase}/` },
        { '@type': 'ListItem', position: 2, name: lang === 'pt' ? 'Distritos' : 'Districts', item: `${siteBase}/` },
        { '@type': 'ListItem', position: 3, name: district, item: `${siteBase}/aluguer/distrito/${slugify(district)}` },
      ]
    });

    // Geo meta tags + correct language attribute for local SEO
    setMeta('geo.region', 'PT');
    setMeta('geo.placename', district);
    document.documentElement.lang = lang;
    return () => {
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
      ['district-medical', 'district-faq', 'district-itemlist', 'district-breadcrumb'].forEach((id) => document.getElementById(id)?.remove());
    };
  }, [district, lang, isEn, districtCounties]);

  const notFound = !district || !c;
  useNoIndexMeta(notFound);

  if (!district || !c) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="text-center max-w-md">
          <MapPin className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">{lang === 'pt' ? 'Distrito não encontrado' : 'District not found'}</h1>
          <Link to="/" className="text-[#0a3452] font-semibold hover:underline">{lang === 'pt' ? '← Voltar ao início' : '← Back home'}</Link>
        </div>
      </div>
    );
  }

  const d = localizedDistrictName(district, lang);
  const startPath = (typeof window !== 'undefined' && window.location.hostname.includes('gestosageis')) ? '/gestosageis' : '/start';
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <OrthocareHeroSearch
        lang={lang}
        hero={{
          badge: d,
          h1: c.h1,
          heroP: c.subtitle,
        }}
      />

      <section className="max-w-5xl mx-auto px-6 py-12">
        <p className="text-slate-700 leading-relaxed text-lg">{c.intro}</p>

        <div className="grid md:grid-cols-2 gap-4 my-10">
          {[
            { icon: Truck, t: lang === 'pt' ? 'Entrega 24h' : '24h delivery', d: lang === 'pt' ? 'Em todo o distrito' : 'Across the district' },
            { icon: Clock, t: lang === 'pt' ? '7 dias/semana' : '7 days/week', d: lang === 'pt' ? 'Incluindo fins de semana' : 'Including weekends' },
            { icon: Shield, t: lang === 'pt' ? 'Certificado e higienizado' : 'Certified & sanitized', d: 'CE • NIF 515948039' },
            { icon: CheckCircle, t: lang === 'pt' ? 'Reserva 100% online' : '100% online booking', d: 'orthocare.pt' },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl p-4">
              <f.icon className="w-6 h-6 text-[#0a3452] shrink-0 mt-0.5" />
              <div><p className="font-semibold text-slate-800">{f.t}</p><p className="text-sm text-slate-500">{f.d}</p></div>
            </div>
          ))}
        </div>

        <PlanFromHomeSection lang={lang} district={district} />

        <h2 className="text-2xl font-bold text-slate-800 mb-4">{c.countiesTitle}</h2>
        {district === 'Lisboa' && (
          <div className="mb-6 bg-[#0a3452]/5 border border-[#0a3452]/20 rounded-xl p-5">
            <p className="font-bold text-slate-800 mb-2">{lang === 'pt' ? 'Grande Lisboa — entregas em:' : lang === 'en' ? 'Greater Lisbon — delivery in:' : lang === 'es' ? 'Gran Lisboa — entrega en:' : lang === 'fr' ? 'Grand Lisbonne — livraison à :' : 'Groß-Lissabon — Lieferung in:'}</p>
            <div className="flex flex-wrap gap-2">
              {['Lisboa','Amadora','Cascais','Loures','Odivelas','Oeiras','Sintra','Vila Franca de Xira','Almada','Seixal','Setúbal'].map((m) => (
                <Link key={m} to={toSitePath(equipmentUrl(lang, 'cadeira-de-rodas', slugify(m)))} className="text-sm text-[#0a3452] hover:underline bg-white px-3 py-1.5 rounded-full border border-[#0a3452]/20">{m}</Link>
              ))}
            </div>
          </div>
        )}
        {district === 'Porto' && (
          <div className="mb-6 bg-[#0a3452]/5 border border-[#0a3452]/20 rounded-xl p-5">
            <p className="font-bold text-slate-800 mb-2">{lang === 'pt' ? 'Grande Porto — entregas em:' : lang === 'en' ? 'Greater Porto — delivery in:' : lang === 'es' ? 'Gran Oporto — entrega en:' : lang === 'fr' ? 'Grand Porto — livraison à :' : 'Groß-Porto — Lieferung in:'}</p>
            <div className="flex flex-wrap gap-2">
              {['Porto','Vila Nova de Gaia','Matosinhos','Maia','Gondomar','Valongo','Póvoa de Varzim','Vila do Conde','Trofa','Santo Tirso'].map((m) => (
                <Link key={m} to={toSitePath(equipmentUrl(lang, 'cadeira-de-rodas', slugify(m)))} className="text-sm text-[#0a3452] hover:underline bg-white px-3 py-1.5 rounded-full border border-[#0a3452]/20">{m}</Link>
              ))}
            </div>
          </div>
        )}
        {districtRegion(district) === 'Algarve' && (
          <div className="mb-6 bg-[#0a3452]/5 border border-[#0a3452]/20 rounded-xl p-5">
            <p className="font-bold text-slate-800 mb-2">{lang === 'pt' ? 'Algarve — aluguer de mobilidade nas principais zonas turísticas:' : lang === 'en' ? 'Algarve — mobility rental in the main tourist areas:' : lang === 'es' ? 'Algarve — alquiler de movilidad en las principales zonas turísticas:' : lang === 'fr' ? "Algarve — location de mobilité dans les principales zones touristiques :" : 'Algarve — Mobilitätsmiete in den wichtigsten Feriengebieten:'}</p>
            <div className="flex flex-wrap gap-2">
              {ALGARVE_CITIES.map((m) => (
                <Link key={m} to={toSitePath(equipmentUrl(lang, 'cadeira-de-rodas', slugify(m)))} className="text-sm text-[#0a3452] hover:underline bg-white px-3 py-1.5 rounded-full border border-[#0a3452]/20">{m}</Link>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-10">
          {districtCounties.map((co) => (
            <Link key={co.id || co.name} to={toSitePath(countyUrl(lang, slugify(co.name)))}
              className="text-sm text-[#0a3452] hover:underline hover:bg-[#0a3452]/5 px-3 py-2 rounded-lg border border-transparent hover:border-[#0a3452]/20">
              {co.name}
            </Link>
          ))}
          {districtCounties.length === 0 && (
            <p className="text-slate-500 col-span-full">{lang === 'pt' ? 'Todos os concelhos do distrito são servidos.' : 'All counties in the district are served.'}</p>
          )}
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-10">
          <p className="text-sm text-slate-600 leading-relaxed">{c.keywordBlock}</p>
        </div>

        <div className="space-y-4 mb-12">
          {c.faqs.map((f, i) => (
            <details key={i} className="bg-white border border-slate-200 rounded-xl p-5 group">
              <summary className="font-semibold text-slate-800 cursor-pointer list-none flex justify-between items-center">
                {f.q}<span className="text-[#0a3452] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-slate-600 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>

        <RealReviewsSection lang={lang} />
      </section>

      <OrthocareFooter language={lang} />
    </div>
  );
}

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
  el.setAttribute('content', content);
}
function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', 'canonical'); document.head.appendChild(el); }
  el.setAttribute('href', href);
}
function setHreflang(langUrls) {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
  const localeMap = {
    pt: ['pt-PT', 'pt'],
    en: ['en-GB', 'en-US', 'en-IE', 'en'],
    es: ['es-ES', 'es'],
    fr: ['fr-FR', 'fr-BE', 'fr'],
    de: ['de-DE', 'de-AT', 'de-CH', 'de'],
    it: ['it-IT', 'it'],
    nl: ['nl-NL', 'nl-BE', 'nl'],
  };
  const entries = [];
  for (const [code, href] of Object.entries(langUrls)) {
    for (const locale of (localeMap[code] || [code])) {
      entries.push({ hreflang: locale, href });
    }
  }
  entries.push({ hreflang: 'x-default', href: langUrls.en || langUrls.pt });
  entries.forEach(({ hreflang, href }) => {
    const l = document.createElement('link');
    l.rel = 'alternate'; l.hreflang = hreflang; l.href = href;
    l.setAttribute('data-page-hreflang', '1');
    document.head.appendChild(l);
  });
}
function injectSchema(id, obj) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement('script'); el.type = 'application/ld+json'; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(obj);
}